// components/carousel.js — generic EmblaCarousel wrapper, lazy-loaded.
//
// Opt-in markup (all data-* hooks — agnostic of partial-specific classes):
//
//   <div data-carousel
//        data-carousel-options='{"loop":true,"align":"start"}'
//        data-carousel-autoplay='{"delay":4000}'              (optional)
//        data-carousel-dots-target=".my-dots">                (optional selector)
//     <div data-carousel-viewport>
//       <ul data-carousel-track>
//         <li data-carousel-slide>…</li>
//       </ul>
//     </div>
//   </div>
//
// Dots can live INSIDE or OUTSIDE the carousel root:
//   - Inside:   [data-carousel-dot] direct descendants of the root
//   - Outside:  add data-carousel-dots-target="selector" on the root,
//               with [data-carousel-dot] inside the matched element
//
// Embla loads via App.preload (lazy) — after first user interaction.
// Until then the CSS fallback (scroll-snap) handles browsing.

export const carousel = {
  selector: '[data-carousel]',
  // NOTE: must be `true` (or omitted) — App.mountComponents skips when
  // reactive === false. The mount-once guard inside mountComponents
  // (`__componentInitialized`) prevents Embla from re-initialising
  // on data changes anyway.
  reactive: true,

  run(el) {
    // Queue the main library — dedupes if other carousels add it too
    App.scripts.lazy.add([App.library('carousel')]);

    // Queue the autoplay plugin only if this instance needs it
    const wantsAutoplay = el.hasAttribute('data-carousel-autoplay');
    if (wantsAutoplay) {
      App.scripts.lazy.add([App.library('carousel-autoplay')]);
    }

    App.preload(() => {}).then(() => {
      if (!window.EmblaCarousel) {
        console.warn('[carousel] EmblaCarousel not on window after lazy load', el);
        return;
      }
      init(el, wantsAutoplay);
    });
  },
};

function parseJsonAttr(el, name) {
  const raw = el.getAttribute(name);
  if (!raw) return null;
  try { return JSON.parse(raw); }
  catch (e) { console.warn(`[carousel] invalid JSON in ${name}`, e); return null; }
}

function init(el, wantsAutoplay) {
  const viewport = el.querySelector('[data-carousel-viewport]') || el;
  const options  = parseJsonAttr(el, 'data-carousel-options') || {};

  // Plugins — only attach autoplay if requested AND the plugin is on window
  const plugins = [];
  if (wantsAutoplay && window.EmblaCarouselAutoplay) {
    const autoplayOpts = parseJsonAttr(el, 'data-carousel-autoplay') || {};
    plugins.push(window.EmblaCarouselAutoplay({
      delay: 4000,
      stopOnInteraction: false,
      ...autoplayOpts,
    }));
  }

  const embla = window.EmblaCarousel(viewport, {
    loop: false,
    align: 'start',
    containScroll: 'trimSnaps',
    ...options,
  }, plugins);

  el.classList.add('is-embla');         // CSS hook: swap from scroll-snap to Embla
  el._embla = embla;                    // programmatic access from DevTools

  // ── Pagination dots ───────────────────────────────────────────
  const dotsTarget = el.getAttribute('data-carousel-dots-target');
  const dotsRoot   = dotsTarget ? document.querySelector(dotsTarget) : el;
  const dots       = dotsRoot ? dotsRoot.querySelectorAll('[data-carousel-dot]') : [];

  if (dots.length) {
    const ACTIVE = 'is-active';
    const setActive = () => {
      const i = embla.selectedScrollSnap();
      dots.forEach((d, idx) => d.classList.toggle(ACTIVE, idx === i));
    };
    dots.forEach((dot, i) => dot.addEventListener('click', () => embla.scrollTo(i)));
    embla.on('select', setActive);
    embla.on('reInit', setActive);
    setActive();
  }
}
